import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { Plus, Search, Pencil, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { getBrands, deleteBrand } from "../../../services/admin/brand";

interface Brand {
    id: number;
    name: string;
}

function AdminBrands() {
    const navigate = useNavigate();
    const [brands, setBrands] = useState<Brand[]>([]);
    const [search, setSearch] = useState("");
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const loadBrands = async () => {
        setIsLoading(true);
        const res = await getBrands();
        if (res.success && Array.isArray(res.data)) {
            setBrands(res.data);
        } else {
            console.error("Failed to load brands", res.error || res.data);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadBrands();
    }, []);

    const handleDeleteClick = (brand: Brand) => {
        setSelectedBrand(brand);
        setShowConfirm(true);
    };

    const confirmDelete = async () => {
        if (selectedBrand) {
            const res = await deleteBrand(selectedBrand.id);
            if (res.success) {
                setBrands((prev) => prev.filter((b) => b.id !== selectedBrand.id));
            } else {
                console.error("Delete failed:", res.error);
            }
            setShowConfirm(false);
            setSelectedBrand(null);
        }
    };

    const handleEditClick = (brand: Brand) => {
        navigate(`/admin/brand/edit-brand/${brand.id}`);
    };

    const filteredBrands = brands.filter((brand) =>
        brand.name.toLowerCase().includes(search.toLowerCase())
    );

    const columns: TableColumn<Brand>[] = [
        {
            name: "ID",
            cell: (_row, index) => (index + 1).toString(),
            sortable: true,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => handleEditClick(row)}
                        className="text-[#009FE3] hover:underline"
                        title="Edit"
                    >
                        <Pencil size={16} />
                    </button>
                    <button
                        onClick={() => handleDeleteClick(row)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
        },
    ];

    return (
        <div className="p-6 space-y-6">
            <h1 className="text-2xl font-semibold text-gray-800">Brands</h1>

            {/* Search + Add Brand */}
            <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="relative w-full max-w-sm">
                    <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search brands..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#009FE3]"
                    />
                </div>

                <Link
                    to="add-brand"
                    className="flex items-center gap-2 px-4 py-2 bg-[#009FE3] text-white rounded-lg hover:opacity-90 transition whitespace-nowrap"
                >
                    <Plus className="w-4 h-4" />
                    Add Brand
                </Link>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow border">
                <DataTable
                    columns={columns}
                    data={filteredBrands}
                    progressPending={isLoading}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    noHeader
                />
            </div>

            {/* Confirm Delete Modal */}
            {showConfirm && selectedBrand && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
                        <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
                        <p className="mb-4">
                            Are you sure you want to delete{" "}
                            <strong>{selectedBrand.name}</strong>?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowConfirm(false);
                                    setSelectedBrand(null);
                                }}
                                className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminBrands;
